import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { useLanguage } from '../context/LanguageContext';
import { SPRING_DEFAULT, REDUCED_MOTION_TRANSITION } from '../utils/springs';
import './ProductCard.css';

const ProductCard = ({ product }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = product.images || [product.image];
  const hasMultipleImages = images.length > 1;
  const reduceMotion = useReducedMotion();
  const imageTransition = reduceMotion ? REDUCED_MOTION_TRANSITION : SPRING_DEFAULT;

  const { language, formatPrice, t } = useLanguage();
  const isComingSoon = Boolean(product.comingSoon);
  const productPath = `/products/${product.id}`;

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const getProductName = () => {
    if (language === 'ro' && product.nameRo) {
      return product.nameRo;
    }
    return product.name;
  };

  const displayName = getProductName();
  const tap = reduceMotion ? undefined : { scale: 0.97 };

  const weightLine = product.weight
    ? isComingSoon
      ? product.weight
      : `${product.weight} · ${t('product.juteBag')}`
    : null;

  return (
    <article className="product-card">
      {isComingSoon ? (
        <span className="product-badge coming-soon">{t('common.comingSoon')}</span>
      ) : (
        <>
          {product.bestseller && (
            <span className="product-badge bestseller">{t('product.bestseller')}</span>
          )}
          {product.new && <span className="product-badge new">{t('product.new')}</span>}
        </>
      )}
      <div className="product-image">
        {hasMultipleImages && (
          <motion.button
            type="button"
            className="image-nav image-nav-prev"
            onClick={handlePrevImage}
            aria-label={t('product.imagePrev')}
            whileTap={tap}
          >
            ‹
          </motion.button>
        )}
        <Link to={productPath} className="product-image-link">
          {product.image ? (
            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={images[currentImageIndex]}
                src={images[currentImageIndex]}
                alt={displayName}
                className="product-img"
                initial={
                  reduceMotion
                    ? { opacity: 0 }
                    : { opacity: 0, x: 12 }
                }
                animate={
                  reduceMotion
                    ? { opacity: 1 }
                    : { opacity: 1, x: 0 }
                }
                exit={
                  reduceMotion
                    ? { opacity: 0 }
                    : { opacity: 0, x: -12 }
                }
                transition={imageTransition}
              />
            </AnimatePresence>
          ) : (
            <div className="product-placeholder">{product.name.charAt(0)}</div>
          )}
        </Link>
        {hasMultipleImages && (
          <motion.button
            type="button"
            className="image-nav image-nav-next"
            onClick={handleNextImage}
            aria-label={t('product.imageNext')}
            whileTap={tap}
          >
            ›
          </motion.button>
        )}
        {hasMultipleImages && (
          <div className="image-dots">
            {images.map((_, index) => (
              <span
                key={index}
                className={`image-dot ${index === currentImageIndex ? 'active' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
      <div className="product-info">
        <Link to={productPath} className="product-name-link">
          <h3 className="product-name">{displayName}</h3>
        </Link>
        {weightLine ? <p className="product-weight">{weightLine}</p> : null}
        <div className="product-price-row">
          {isComingSoon ? (
            <span className="product-coming-soon-label">{t('common.comingSoon')}</span>
          ) : (
            <>
              <div className="product-price-block">
                <span className="product-price">{formatPrice(product.price)}</span>
                {product.pricePerKg != null && (
                  <span className="product-unit-price">
                    {formatPrice(product.pricePerKg)} {t('product.perKg')}
                  </span>
                )}
              </div>
              <Link to={productPath} className="product-order-cta">
                {t('product.orderNow')} →
              </Link>
            </>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
